import React, { useEffect, useRef, useState } from "react";
import PageLayout from "@/components/layout/HomePageLayout";
import { X, Menu, ChevronRight } from "lucide-react";

type TocItem = { id: string; title: string; level: number };

const LearnMoreVetting: React.FC = () => {
  const articleRef = useRef<HTMLDivElement>(null);
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>("purpose");
  const navRef = useRef<HTMLDivElement>(null);
  const [markerTop, setMarkerTop] = useState<number>(0);
  const [markerHeight, setMarkerHeight] = useState<number>(0);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [mobileMarkerTop, setMobileMarkerTop] = useState<number>(0);
  const [mobileMarkerHeight, setMobileMarkerHeight] = useState<number>(0);
  const [showMobileBar, setShowMobileBar] = useState<boolean>(false);

  useEffect(() => {
    const root = articleRef.current;
    if (!root) return;
    const elements = Array.from(
      root.querySelectorAll<HTMLElement>("h2[id], h3[id]")
    );
    const items: TocItem[] = elements.map((el) => ({
      id: el.id,
      title: el.innerText,
      level: el.tagName === "H2" ? 2 : 3,
    }));
    setTocItems(items);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (a.target as HTMLElement).offsetTop - (b.target as HTMLElement).offsetTop);
        if (visible[0]) setActiveId((visible[0].target as HTMLElement).id);
      },
      {
        rootMargin: "0px 0px -60% 0px",
        threshold: [0, 1.0],
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Position the left highlight bar to align with the active link
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const activeLink = nav.querySelector<HTMLAnchorElement>(`a[data-id='${activeId}']`);
    if (activeLink) {
      const navRect = nav.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();
      setMarkerTop(linkRect.top - navRect.top);
      setMarkerHeight(linkRect.height);
    }
    // also update mobile marker if panel is open
    const mnav = mobileNavRef.current;
    if (mobileOpen && mnav) {
      const act = mnav.querySelector<HTMLAnchorElement>(`a[data-id='${activeId}']`);
      if (act) {
        const nrect = mnav.getBoundingClientRect();
        const lrect = act.getBoundingClientRect();
        setMobileMarkerTop(lrect.top - nrect.top);
        setMobileMarkerHeight(lrect.height);
      }
    }
  }, [activeId, tocItems.length, mobileOpen]);

  useEffect(() => {
    const onResize = () => {
      // recompute marker position on resize
      const nav = navRef.current;
      if (!nav) return;
      const activeLink = nav.querySelector<HTMLAnchorElement>(`a[data-id='${activeId}']`);
      if (activeLink) {
        const navRect = nav.getBoundingClientRect();
        const linkRect = activeLink.getBoundingClientRect();
        setMarkerTop(linkRect.top - navRect.top);
        setMarkerHeight(linkRect.height);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [activeId]);

  // Show mobile sticky bar only after user has scrolled a bit
  useEffect(() => {
    const onScroll = () => {
      setShowMobileBar(window.scrollY > 24);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <PageLayout>
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-14">
        {/* Mobile trigger (hamburger) with glass header effect - visible only after scroll */}
        <div
          className={`lg:hidden sticky top-14 z-50 -mx-4 px-4 pt-2 pb-3 transition-opacity duration-200 ${
            showMobileBar ? "glass-header-enhanced border-b opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="inline-flex items-center gap-2 py-1 text-sm rounded shrink-0"
              aria-label="Open table of contents"
            >
              <Menu size={18} />
              <span>On this page</span>
            </button>
            <div className="flex items-center gap-1 text-sm text-muted-foreground min-w-0">
              <ChevronRight size={14} className="opacity-70 shrink-0" />
              <a
                href={`#${activeId}`}
                className="truncate hover:text-foreground max-w-[65vw]"
                title={tocItems.find((i) => i.id === activeId)?.title || ""}
              >
                {tocItems.find((i) => i.id === activeId)?.title || ""}
              </a>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-10">
          {/* Main content */}
          <article ref={articleRef} className="prose prose-neutral max-w-none dark:prose-invert docs-content">
            {/* Invisible sentinel to ensure the first heading never hides under sticky bars */}
            <div id="_top" className="h-0"></div>
            <p className="text-sm text-muted-foreground">Getting started</p>
            <h1 className="mb-2">World-Class Designer Vetting Process</h1>
            <p className="mt-0 text-sm text-muted-foreground">
              Issued by: <strong>World Class Design</strong>
              <br />Subject: Overview of the Three-Step Vetting Process for Designer Verification
            </p>

            <section id="purpose">
              <h2>Purpose of This Document</h2>
              <p>
                Design is full of noise. Trust is what opens doors. This process helps great designers get seen,
                taken seriously, and verified, not by algorithms, but by people who’ve actually hired at world-class
                companies.
              </p>
            </section>

            <section id="world-class">
              <h2>Prove You’re World-Class. No Big Logo Required</h2>
              <h3>What is World-class Design(er)</h3>
              <p>
                World-class design is the ability to deliver exceptional work that would pass a mid-to-senior level
                hiring bar at top-tier global companies (e.g., Google, Shopify, or equivalent). It's defined by:
              </p>
              <ul>
                <li>
                  <strong>Impactful Problem-Solving</strong>: Creating user-centered solutions that address clear
                  challenges and deliver measurable outcomes (e.g., improved user retention or streamlined workflows).
                </li>
                <li>
                  <strong>Strategic Thinking</strong>: Aligning design decisions with business or product goals, showing
                  awareness of trade-offs and priorities.
                </li>
                <li>
                  <strong>Polished Craft</strong>: Producing visually and functionally cohesive work, with attention to
                  typography, layout, and usability (e.g., WCAG-compliant designs).
                </li>
                <li>
                  <strong>Adaptability</strong>: Demonstrating versatility across projects, industries, or roles,
                  whether from startups, agencies, freelance work, or large companies.
                </li>
                <li>
                  <strong>Clear Communication</strong>: Articulating process, decisions, and impact in a way that's
                  compelling to stakeholders, regardless of public recognition or fame.
                </li>
              </ul>
              <p>
                Excellence isn't tied to big company names or public profiles—it’s about the quality of your work and
                thinking, accessible to designers from all backgrounds.
              </p>
            </section>

            <section id="process">
              <h2>The Vetting Process</h2>
              <p>
                This is the only design verification process inspired by how FAANG-level companies evaluate talent.
                Built by real recruiters and design leads, it helps serious designers prove their credibility, even
                without a famous company on their resume.
              </p>
              <p>
                This memo outlines the official three-step vetting process that determines whether a designer qualifies
                for the Verified World-Class Designer status on our platform. This process is based on best-in-class
                hiring practices used by leading global technology companies, including Google, Meta, Apple, Netflix,
                Amazon, and Shopify. This document serves both internal stakeholders and candidates undergoing
                evaluation. It is designed to ensure clarity, fairness, and transparency at every stage of assessment.
              </p>
              <h3 id="summary">Summary of the Vetting Process</h3>
              <p>
                The vetting process is divided into three distinct stages. Each stage is mandatory and must be passed
                successfully to move forward. Only candidates who meet the benchmark at all three levels will be
                awarded the Verified World-Class Designer badge.
              </p>
              <ul>
                <li>Portfolio Review</li>
                <li>Recruiter Assessment</li>
                <li>Hiring Manager Interview</li>
              </ul>
              <p>Each stage is mandatory and must be passed to proceed. Only candidates who succeed in all three are verified.</p>
            </section>

            <section id="stage-0">
              <h2>Stage 0: Accessing the Verification Process</h2>
              <p>
                <strong>Goal:</strong> Set expectations, confirm eligibility, and secure payment to begin the
                evaluation. Before entering the official vetting process, candidates are required to confirm their
                intent and pay the verification fee. This ensures a high-signal process, avoids unnecessary no-shows,
                and compensates the experts involved in evaluating each candidate.
              </p>
              <h3 id="cost-structure-policy">Cost Structure &amp; Policy</h3>
              <p>The verification process costs $399 and includes two expert-led evaluation steps:</p>
              <ul>
                <li>Recruiter Assessment</li>
                <li>Hiring Manager Review</li>
              </ul>
              <ul>
                <li>The Portfolio Review (Stage 1) is free and open to all designers. It acts as the first filter.</li>
                <li>Once the candidate passes Stage 1, they are invited to proceed with payment to unlock Stage 2 and Stage 3.</li>
                <li>If a candidate fails at any paid stage, they will need to repay the fee to apply again. This ensures the process remains fair, high-quality, and sustainable.</li>
              </ul>
              <h3 id="why-we-charge">Why We Charge</h3>
              <p>This is a pay-for-expertise process, not pay-to-play. The fee ensures:</p>
              <ul>
                <li>Professional-level, actionable feedback.</li>
                <li>Fair compensation for evaluators (60% of fees cover expert time, 30% platform maintenance, 10% operational costs).</li>
                <li>A serious, merit-based pipeline.</li>
              </ul>
            </section>

            <section id="stage-1">
              <h2>Stage 1: Portfolio Review</h2>
              <p>
                <strong>Conducted by:</strong> Senior Designers from our Verification Panel
                <br />
                <strong>Goal:</strong> Evaluate the designer’s ability to present, explain, and frame their work in a
                compelling and professional manner.
                <br />
                <strong>Passing Score:</strong> 6 out of 10
              </p>
              <ul>
                <li><strong>Clear Problem Framing</strong>: Each project clearly states the problem, challenge, or goal it set out to solve. No fluff, no vague language.</li>
                <li><strong>Context &amp; Role Clarity</strong>: It’s obvious what the designer contributed. The role, team setup, tools, and constraints are explained upfront.</li>
                <li><strong>Process Transparency</strong>: The designer shows their thinking and decision-making. We see not just what was done, but why.</li>
                <li><strong>Evidence of Outcomes</strong>: The work led to measurable results or clear improvements. Could be metrics, user feedback, or business impact.</li>
                <li><strong>Strong Visual Craft</strong>: Typography, layout, spacing, alignment, and consistency meet a high professional bar. Doesn’t need to be flashy, but must be clean and deliberate.</li>
                <li><strong>Information Architecture</strong>: The portfolio itself is well-organized. Navigation is intuitive, sections are structured, and content is easy to follow.</li>
                <li><strong>Written Communication</strong>: Case studies are written clearly and concisely. No jargon, no rambling. The writing feels intentional and readable.</li>
                <li><strong>Strategic Awareness</strong>: The designer connects their work to broader product or business goals. Not just UI polish, but thoughtful design decisions.</li>
                <li><strong>Variety &amp; Depth</strong>: Projects show a range of skills or design types. Ideally includes at least one deep dive, not just surface-level snapshots.</li>
                <li><strong>Real-World Work or Simulation</strong>: The projects feel grounded—either from real clients, jobs, internships, or robust self-initiated work with depth and realism.</li>
              </ul>
              <h3 id="passing-criteria-s1">Passing Criteria</h3>
              <p>
                If the portfolio meets 6 out of these 10, the designer proceeds to the next stage. No individual
                criterion is mandatory, but a well-rounded portfolio should hit multiple strong signals.
              </p>
              <h3 id="outcomes-s1">Outcomes</h3>
              <p><strong>If Rejected:</strong> The candidate receives a clear rejection email along with a short, structured summary of areas that need improvement. While this isn’t fully personalized feedback, it’s more than just a generic “no.” Designers are also encouraged to reapply once they’ve made tangible improvements.</p>
              <p><strong>If Approved:</strong> The designer's profile is automatically marked as Verified, their dashboard is updated with full access, and the public profile becomes active immediately.</p>
            </section>

            <section id="stage-2">
              <h2>Stage 2: Recruiter Assessment</h2>
              <p>
                <strong>Conducted by:</strong> Former or Current FAANG-Level Recruiters
                <br />
                <strong>Goal:</strong> Assess how the candidate would perform in a recruiter screening at a top-tier
                company. This stage simulates the recruiter screen used in top hiring pipelines. The focus is on
                evaluating how well the candidate communicates their experience, presents their trajectory, and signals
                readiness for high-performance teams. This is not a design review—it’s a credibility check.
                <br />
                <strong>Passing Score:</strong> 5 out of 8
              </p>
              <ul>
                <li><strong>Resume &amp; LinkedIn Clarity</strong>: Experience, skills, and value are communicated clearly. No jargon, no ambiguity.</li>
                <li><strong>Role Level Fit</strong>: The candidate’s experience aligns with expectations for mid-level or senior roles. Entry-level signals may not qualify.</li>
                <li><strong>Relevance of Experience</strong>: The work reflects real-world complexity, team dynamics, or product-level thinking—even if not at a big-name company.</li>
                <li><strong>Signals of Ownership</strong>: Demonstrates initiative, autonomy, or leadership beyond task-following.</li>
                <li><strong>Industry or Domain Breadth</strong>: Exposure to varied problem spaces, showing adaptability and learning agility.</li>
                <li><strong>Communication &amp; Professionalism</strong>: Public-facing presence (e.g. LinkedIn, portfolio, bio) is professional, well-written, and clear.</li>
                <li><strong>Career Progression</strong>: Shows upward or intentional movement. Gaps or switches are explained in a coherent story.</li>
                <li><strong>Bonus: Recognition or Thought Leadership</strong>: Mentorship, talks, writing, or recognitions are not required but strengthen the profile.</li>
              </ul>
              <h3 id="passing-criteria-s2">Passing Criteria</h3>
              <p>Candidates who meet 5 out of these 8 criteria move to the next stage.</p>
              <h3 id="outcomes-s2">Outcomes</h3>
              <p><strong>If Rejected:</strong> The candidate receives a detailed written report via email, outlining specific reasons for non-approval, which criteria were not met, and a personalized roadmap for improvement. Candidates are welcome to reapply once these areas are addressed.</p>
              <p><strong>If Approved:</strong> The candidate receives a personalized report highlighting what they did well and, if applicable, areas to refine. They are then invited to schedule a final-stage conversation with a hiring manager. This report is also saved in the system to support the hiring manager’s prep and ensure continuity in evaluation.</p>
            </section>

            <section id="stage-3">
              <h2>Stage 3: Hiring Manager Interview</h2>
              <p>
                <strong>Conducted by:</strong> Former or Current Design Manager or Director from World-Class Companies
                <br />
                <strong>Goal:</strong> Deliver a final, expert-level evaluation of the candidate’s design maturity,
                communication, and strategic thinking. This is not a live interview. Instead, it’s a one-way video
                review. A design leader will assess the candidate’s portfolio, recruiter feedback, and overall profile,
                then record a personalized video providing a final evaluation—just like they would when advocating for
                or rejecting a candidate internally.
                <br />
                <strong>Passing Score:</strong> 4 out of 6
              </p>
              <ul>
                <li><strong>Depth of Experience</strong>: Does the designer show full ownership and understanding of the work, from brief to execution to impact?</li>
                <li><strong>Design Judgment</strong>: Are trade-offs explained with clarity? Is there evidence of mature, principled decision-making?</li>
                <li><strong>Strategic Thinking</strong>: Does the work connect to broader goals (user, business, product)? Is the designer able to think beyond UI?</li>
                <li><strong>Cross-Functional Fluency</strong>: Is there proof the designer knows how to collaborate and influence across disciplines?</li>
                <li><strong>Growth Mindset &amp; Self-Awareness</strong>: Does the designer acknowledge challenges or mistakes and show how they’ve grown from them?</li>
                <li><strong>Communication &amp; Presence</strong>: Is the work presented with clarity, confidence, and structure—even when the designer is not in the room?</li>
              </ul>
              <h3 id="passing-criteria-s3">Passing Criteria</h3>
              <p>If the candidate meets 4 out of these 6, they are verified.</p>
              <h3 id="outcomes-s3">Outcomes</h3>
              <p><strong>If Rejected:</strong> The candidate receives a personalized video recording from the hiring manager explaining the decision, along with a private written note summarizing what fell short and offering clear next steps for improvement. This note is not made public. Candidates may reapply after 30 days with a stronger case.</p>
              <p><strong>If Approved:</strong> The candidate receives a personalized video from the hiring manager summarizing their strengths and why they passed. A public endorsement note is added to their profile (visible to companies and peers), reinforcing their world-class status. They also receive the verified badge, access to referral opportunities, and may be invited for platform features or advanced badges.</p>
            </section>

            <section id="fairness">
              <h2>Diversity &amp; Fairness</h2>
              <p>
                We know design excellence doesn’t always come from well-known companies or traditional paths. This
                process is built to reduce bias by focusing on actual work, real signals, and clear standards—not hype,
                fame, or follower count.
              </p>
            </section>

            <section id="appeal">
              <h2>Appeal Process</h2>
              <p>
                Candidates who believe their evaluation was unfair may submit an appeal within 7 days of rejection.
                Appeals are reviewed by a different evaluator within 5 business days, with a detailed response
                explaining the outcome.
              </p>
            </section>

            <section id="final-notes">
              <h2>Final Notes</h2>
              <ul>
                <li>
                  <strong>Total Duration:</strong> 3–5 business days (Stage 1: 1–2 days, Stage 2: 1–2 days, Stage 3:
                  1–3 days). Delays are communicated within 24 hours with a revised timeline.
                </li>
                <li>
                  <strong>Feedback:</strong> Candidates receive feedback after each stage, regardless of outcome.
                </li>
                <li>
                  <strong>Confidentiality:</strong> All information shared during this process is confidential and will
                  not be made public unless the candidate gives written consent.
                </li>
              </ul>
            </section>
          </article>

          {/* On this page */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <h4 className="text-sm font-semibold text-muted-foreground mb-2">On this page</h4>
              <div ref={navRef} className="relative text-sm pl-4">
                <div className="absolute left-0 top-0 bottom-0 w-px bg-border" aria-hidden />
                <div
                  className="absolute left-0 w-[2px] bg-foreground rounded transition-all duration-200"
                  style={{ top: markerTop, height: markerHeight }}
                  aria-hidden
                />
                <nav className="space-y-1.5">
                  {tocItems.map((item) => {
                    const isActive = activeId === item.id;
                    return (
                      <a
                        key={item.id}
                        data-id={item.id}
                        href={`#${item.id}`}
                        className={`block py-1 -ml-2 pl-2 ${
                          isActive ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"
                        } ${item.level === 3 ? "pl-6" : "pl-2"}`}
                      >
                        {item.title}
                      </a>
                    );
                  })}
                </nav>
              </div>
            </div>
          </aside>
        </div>
        {/* Mobile left drawer for On This Page */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
              aria-hidden
            />
            <div className="absolute left-0 top-0 h-full w-[85vw] max-w-[420px] bg-background border-r shadow-xl flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="text-sm font-semibold">On this page</div>
                <button aria-label="Close" className="p-2 rounded hover:bg-muted" onClick={() => setMobileOpen(false)}>
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <div ref={mobileNavRef} className="relative text-sm pl-4">
                  <div className="absolute left-0 top-0 bottom-0 w-px bg-border" aria-hidden />
                  <div
                    className="absolute left-0 w-[2px] bg-foreground rounded transition-all duration-200"
                    style={{ top: mobileMarkerTop, height: mobileMarkerHeight }}
                    aria-hidden
                  />
                  <nav className="space-y-1.5">
                    {tocItems.map((item) => {
                      const isActive = activeId === item.id;
                      return (
                        <a
                          key={item.id}
                          data-id={item.id}
                          href={`#${item.id}`}
                          onClick={() => setMobileOpen(false)}
                          className={`block py-1 -ml-2 pl-2 ${
                            isActive ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"
                          } ${item.level === 3 ? "pl-6" : "pl-2"}`}
                        >
                          {item.title}
                        </a>
                      );
                    })}
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default LearnMoreVetting;


